import {
  type CollectionType,
  type DirectusClient,
  type Query,
  type RegularCollections,
  type RestClient,
} from '@directus/sdk';
import { type Column, type ItemTypeFromQuery } from '@components/table/types';
import { usePaginatedDirectusData } from '@hooks/usePaginatedDirectusData.ts';
import { useSelection } from '@hooks/useSelection.ts';
import {
  type ForwardedRef,
  type MouseEvent,
  useImperativeHandle,
  useMemo,
} from 'react';
import SortingPaginatedTable, {
  type SortingPaginatedTableProps,
} from '@components/table/SortingPaginatedTable.tsx';
import type { SortSpec } from '@src/types/sort';

export type ColumnOverrides<
  Data,
  AdditionalProps,
  Keys extends string,
> = Record<Keys, Omit<Column<Data, AdditionalProps>, 'identifier'>>;

export type DirectusTableProps<
  Schema extends object,
  Collection extends RegularCollections<Schema>,
  TQuery extends Query<Schema, CollectionType<Schema, Collection>>,
  AdditionalProps extends Record<string, unknown> = Record<string, never>,
  TItem = ItemTypeFromQuery<Schema, Collection, TQuery>,
  ColNames extends string = string,
> = {
  readonly collection: Collection;
  readonly query?: Omit<TQuery, 'sort' | 'limit' | 'page' | 'offset'>;
  readonly directusClient: DirectusClient<Schema> & RestClient<Schema>;
  readonly selectedColumns: ColNames[];
  readonly columnOverrides: ColumnOverrides<TItem, AdditionalProps, ColNames>;
  readonly itemsPerPage?: number;
  readonly className?: string;
  readonly allowSelection?: boolean;
  readonly additionalProps?: AdditionalProps;
  readonly onRowClick?: (
    rowData: TItem,
    event: MouseEvent<HTMLTableRowElement>,
  ) => void;
  readonly getRowLink?: (id: string) => string;
  readonly renderTopHeader?: (total: number) => JSX.Element;
  readonly initialSort?: SortSpec;
  readonly getId: (rowData: TItem) => string;
} & Pick<
  SortingPaginatedTableProps<TItem[] | null, AdditionalProps>,
  'embedded'
>;

const DEFAULT_QUERY = { fields: ['*'] } as const;

const EMPTY_SELECTION_PROPS = {
  selectedIds: undefined,
  setSelectedIds: undefined,
  toggleCurrentPage: undefined,
};

export type DirectusTableHandle = {
  reload: (withoutCache: boolean) => void;
};
/**
 * A table that fetches data from Directus and displays it in a paginated and sortable way.
 *
 * Example usage:
 * <DirectusTable<CustomDirectusTypes, 'animal', typeof query>
 *   collection="animal"
 *   directusClient={directusClient.client}
 *   query={query}
 *   selectedColumns={[
 *     'name',
 *     'species',
 *     'age',
 *      { owner: ['name', 'avatar'] },
 *   ]}
 *   columnOverrides={{
 *     'owner': {
 *       identifier: 'owner',
 *       sortable: true,
 *       headContent: 'Owner',
 *       renderCell: ({ rowData }) => (
 *         <div>
 *           <img src={getAssetUrl(rowData.owner.avatar)} />
 *           {rowData.owner.name}
 *         </div>
 *       ),
 *     },
 *   }}
 *   className={'mt-4'}
 * />
 *
 */
export default function DirectusTable<
  Schema extends object,
  Collection extends RegularCollections<Schema>,
  TQuery extends Query<Schema, CollectionType<Schema, Collection>> = {
    fields: ['*'];
  },
  AdditionalProps extends Record<string, unknown> = Record<string, never>,
  TItem = ItemTypeFromQuery<Schema, Collection, TQuery>,
>({
  directusClient,
  collection,
  query = DEFAULT_QUERY as unknown as TQuery,
  selectedColumns,
  columnOverrides = {},
  itemsPerPage = 10,
  className,
  allowSelection = false,
  additionalProps,
  onRowClick,
  getRowLink,
  renderTopHeader,
  initialSort,
  getId,
  ref,
  ...rest
}: DirectusTableProps<Schema, Collection, TQuery, AdditionalProps, TItem> & {
  ref?: ForwardedRef<DirectusTableHandle | undefined>;
}) {
  const {
    data,
    totalCount,
    error: dataError,
    loading,
    currentPage,
    numberOfAvailablePages,
    setCurrentPage,
    sort,
    setSort,
    reload,
  } = usePaginatedDirectusData<Schema, Collection, TQuery, TItem>({
    itemsPerPage,
    directusClient,
    collection,
    query,
    initialSort,
  });

  useImperativeHandle(ref, () => ({ reload }), [reload]);

  const columns = useMemo(
    () =>
      selectedColumns.map((identifier) => {
        const override = columnOverrides[identifier];
        return override ? { ...override, identifier } : null;
      }),
    [selectedColumns, columnOverrides],
  );

  const selectionProps = useSelection(query);

  if (dataError) {
    throw dataError;
  }

  return (
    <>
      {renderTopHeader?.(totalCount)}
      <SortingPaginatedTable
        sort={sort}
        setSort={setSort}
        columns={columns}
        data={data ?? []}
        getId={getId}
        loading={loading}
        totalCount={totalCount}
        currentPage={currentPage}
        numberOfAvailablePages={numberOfAvailablePages}
        setCurrentPage={setCurrentPage}
        className={className}
        itemsPerPage={itemsPerPage}
        onRowClick={onRowClick}
        getRowLink={getRowLink}
        additionalProps={additionalProps}
        hideResultCounter
        {...(allowSelection ? selectionProps : EMPTY_SELECTION_PROPS)}
        {...rest}
      />
    </>
  );
}
